import { themeAtom } from "../../../shared/atoms";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { FormEvent, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  mailbox: string;
  saving: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSave: (mailbox: string) => void | Promise<void>;
};

const AdminKeysSecretsMailboxModal = ({ mailbox, saving, errorMessage, onClose, onSave }: Props) => {
  const theme = useAtomValue(themeAtom);
  const [mailboxInput, setMailboxInput] = useState(mailbox);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  useEffect(() => {
    setMailboxInput(mailbox);
    setValidationMessage(null);
  }, [mailbox]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedMailbox = mailboxInput.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedMailbox)) {
      setValidationMessage("Mailbox must be a valid email address.");
      return;
    }

    setValidationMessage(null);
    await onSave(normalizedMailbox);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-keys-secrets-mailbox-modal-title"
      data-test="admin-keys-secrets-mailbox-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-3 py-4"
    >
      <section
        className={twMerge(
          "flex max-h-[88vh] w-full max-w-[520px] flex-col bg-white shadow-2xl dark:bg-gray-800",
          theme.home.tailwind.textDefault,
        )}
      >
        <header className="flex flex-none items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h2 id="admin-keys-secrets-mailbox-modal-title" className="text-base font-semibold">
              Change mailbox
            </h2>
            <p className="mt-1 text-xs text-[#637381]">Keys are read from this mailbox.</p>
          </div>
          <button
            type="button"
            aria-label="Close mailbox modal"
            data-test="admin-keys-secrets-mailbox-modal-x"
            disabled={saving}
            onClick={onClose}
            className="inline-flex h-8 w-8 flex-none items-center justify-center text-[#4A5655] transition-colors hover:bg-[#E9F4F3] disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5 dark:text-gray-400" />
          </button>
        </header>

        <form onSubmit={handleSubmit} data-test="admin-keys-secrets-mailbox-form">
          <div className="px-5 py-4">
            <label
              htmlFor="admin-keys-secrets-mailbox-input"
              className="text-xs font-semibold text-[#4A5655] dark:text-gray-400"
            >
              Current mailbox
            </label>
            <input
              id="admin-keys-secrets-mailbox-input"
              data-test="admin-keys-secrets-mailbox-input"
              type="email"
              value={mailboxInput}
              disabled={saving}
              onChange={(event) => setMailboxInput(event.target.value)}
              className="mt-2 h-10 w-full border border-[#B7C9C4] bg-white px-3 text-sm outline-none transition-colors focus:border-green disabled:cursor-not-allowed disabled:bg-[#F8FBFA] dark:border-gray-500 dark:bg-gray-900"
            />
          </div>

          {(validationMessage || errorMessage) && (
            <div
              role="alert"
              data-test="admin-keys-secrets-mailbox-modal-error"
              className="border-t border-[#F59692] bg-[#FFEAED] px-5 py-3 text-sm text-[#BF0002]"
            >
              {validationMessage ?? errorMessage}
            </div>
          )}

          <footer className="flex flex-none justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
            <button
              type="button"
              data-test="admin-keys-secrets-mailbox-close"
              disabled={saving}
              onClick={onClose}
              className="inline-flex h-10 items-center justify-center border border-gray-300 px-4 text-sm font-semibold text-[#4A5655] transition-colors hover:bg-[#F8FBFA] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
            >
              Close
            </button>
            <button
              type="submit"
              data-test="admin-keys-secrets-mailbox-save"
              disabled={saving}
              className="inline-flex h-10 items-center justify-center bg-green px-4 text-sm font-semibold text-white transition-colors hover:bg-[#166b60] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
};

export default AdminKeysSecretsMailboxModal;
